import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Plane, Hotel, Car, FileText, Calendar, MapPin, AlertCircle } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../components/Toast';
import { TOAST_MESSAGES, CONFIRM_MESSAGES } from '../utils/toastMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const stored = sessionStorage.getItem('assistant');
  if (stored) {
    try {
      const assistant = JSON.parse(stored);
      return { 'x-assistant-session': assistant.id };
    } catch (e) {}
  }
  return {};
};

// Provider options for each travel type
const PROVIDER_OPTIONS = {
  flight: [
    'American Airlines',
    'Delta Air Lines',
    'United Airlines',
    'Southwest Airlines',
    'JetBlue Airways',
    'Alaska Airlines',
    'Spirit Airlines',
    'Frontier Airlines',
    'Other'
  ],
  hotel: [
    'Marriott',
    'Hilton',
    'Hyatt',
    'InterContinental (IHG)',
    'Wyndham',
    'Choice Hotels',
    'Best Western',
    'Radisson',
    'Airbnb',
    'VRBO',
    'Other'
  ],
  car_rental: [
    'Enterprise',
    'Hertz',
    'Avis',
    'Budget',
    'National',
    'Alamo',
    'Dollar',
    'Thrifty',
    'Sixt',
    'Turo',
    'Other'
  ]
};

const TravelBinderPage = () => {
  const navigate = useNavigate();
  const [travelItems, setTravelItems] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [executivePreferences, setExecutivePreferences] = useState({}); // Store preferences by executive_id
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    executive_id: '',
    trip_name: '',
    type: 'flight',
    provider: '',
    start_date: '',
    end_date: '',
    confirmation_number: '',
    notes: '',
    // Smart fields for Flight
    flight_number: '',
    departure_airport: '',
    arrival_airport: '',
    seat_preference: '',
    // Smart fields for Hotel
    hotel_address: '',
    room_type: '',
    check_in_time: '',
    check_out_time: '',
    special_requests: '',
    // Smart fields for Car Rental
    pickup_location: '',
    dropoff_location: '',
    car_type: '',
    insurance_needed: ''
  });
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showTripModal, setShowTripModal] = useState(false);
  const { success, error: showError } = useToast();

  useEffect(() => {
    fetchTravelItems();
    fetchExecutives();
  }, []);

  const fetchTravelItems = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/assistant/travel`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setTravelItems(data.travel_items || []);
      } else if (res.status === 401) {
        navigate('/assistant-login');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching travel items:', err);
      setLoading(false);
    }
  };

  const fetchExecutives = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/executives`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        const execs = data.executives || [];
        setExecutives(execs);
        
        // Build preferences map from executives data
        const prefsMap = {};
        execs.forEach(exec => {
          prefsMap[exec.id] = {
            travel_preferences: exec.travel_preferences_json || exec.travel_preferences || {},
            tsa_precheck: exec.travel_preferences_json?.tsa_precheck ?? exec.travel_preferences?.tsa_precheck ?? false,
            seat_preference: exec.travel_preferences_json?.seat_preference || exec.travel_preferences?.seat_preference || '',
            preferred_airlines: exec.travel_preferences_json?.preferred_airlines || exec.travel_preferences?.preferred_airlines || [],
            preferred_hotels: exec.travel_preferences_json?.preferred_hotels || exec.travel_preferences?.preferred_hotels || [],
            preferred_car_types: exec.travel_preferences_json?.preferred_car_types || exec.travel_preferences?.preferred_car_types || [],
            loyalty_programs: exec.travel_preferences_json?.loyalty_programs || exec.travel_preferences?.loyalty_programs || {},
          };
        });
        setExecutivePreferences(prefsMap);
      }
    } catch (err) {
      console.error('Error fetching executives:', err);
    }
  };

  const handleArchiveClick = (item) => {
    setItemToArchive(item);
    setShowArchiveConfirm(true);
  };

  const handleArchive = async () => {
    if (!itemToArchive) return;
    
    setIsArchiving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/travel/${itemToArchive.id}/archive`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        setShowArchiveConfirm(false);
        // Remove archived item from view immediately
        setTravelItems(prevItems => 
          prevItems.filter(item => item.id !== itemToArchive.id)
        );
        success(TOAST_MESSAGES.TASK_ARCHIVED.title, TOAST_MESSAGES.TASK_ARCHIVED.message);
      } else {
        showError('Failed to archive', 'Please try again.');
      }
    } catch (err) {
      console.error('Archive error:', err);
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    }
    setIsArchiving(false);
  };

  const handleCreate = async () => {
    setError('');
    
    if (!formData.trip_name.trim() || !formData.executive_id) {
      setError('Trip name and executive are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };

      const res = await fetch(`${BACKEND_URL}/api/assistant/travel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify(payload)
      });

      if (res.status === 401) {
        setError('Session expired. Please log in again.');
        setTimeout(() => navigate('/assistant-login'), 2000);
        return;
      }

      if (!res.ok) {
        if (res.status === 500) {
          setError('Server error. Please try again or contact admin.');
        } else if (res.status === 403) {
          setError('Unauthorized. Please check your assigned executives.');
        } else {
          setError(`Error ${res.status}: Failed to create travel item`);
        }
        return;
      }

      const data = await res.json();
      
      if (data.ok) {
        setShowCreateModal(false);
        setFormData({
          executive_id: '',
          trip_name: '',
          type: 'flight',
          provider: '',
          start_date: '',
          end_date: '',
          confirmation_number: '',
          notes: ''
        });
        fetchTravelItems();
        success('Travel item added', "It's been added to your binder.");
      } else {
        setError(data.message || 'Failed to create travel item');
      }
    } catch (err) {
      console.error('Travel item creation error:', err);
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'flight': return <Plane size={20} />;
      case 'hotel': return <Hotel size={20} />;
      case 'car_rental': return <Car size={20} />;
      case 'itinerary': return <FileText size={20} />;
      default: return <MapPin size={20} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'flight': return '#3B82F6';
      case 'hotel': return '#8B5CF6';
      case 'car_rental': return '#F59E0B';
      case 'itinerary': return '#10B981';
      default: return '#6B7280';
    }
  };

  const filteredItems = filterType === 'all' 
    ? travelItems 
    : travelItems.filter(item => item.type === filterType);

  const groupedItems = filteredItems.reduce((acc, item) => {
    const execName = item.executive_name || 'Unknown';
    if (!acc[execName]) {
      acc[execName] = [];
    }
    acc[execName].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FA', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6B7280' }}>Loading travel items...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '20px 32px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => navigate('/a-dashboard')}
                style={{
                  padding: '8px',
                  background: 'transparent',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ArrowLeft size={18} color="#6B7280" />
              </button>
              <div>
                <h1 style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '600', margin: '0 0 4px 0' }}>
                  Travel Binder
                </h1>
                <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                  Manage executive travel arrangements
                </p>
              </div>
            </div>
            
            <button
              onClick={() => {
                setFormData({
                  executive_id: executives[0]?.id || '',
                  trip_name: '',
                  type: 'flight',
                  provider: '',
                  start_date: '',
                  end_date: '',
                  confirmation_number: '',
                  notes: ''
                });
                setError('');
                setShowCreateModal(true);
              }}
              style={{
                padding: '10px 16px',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Plus size={18} />
              Add Travel Item
            </button>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {['all', 'flight', 'hotel', 'car_rental', 'itinerary', 'other'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                style={{
                  padding: '6px 12px',
                  fontSize: '13px',
                  border: filterType === type ? '1px solid #3B82F6' : '1px solid #E5E7EB',
                  background: filterType === type ? '#EFF6FF' : '#FFFFFF',
                  color: filterType === type ? '#3B82F6' : '#6B7280',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: filterType === type ? '500' : '400',
                  textTransform: 'capitalize'
                }}
              >
                {type.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px' }}>
        {Object.keys(groupedItems).length === 0 ? (
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            padding: '48px',
            textAlign: 'center'
          }}>
            <Plane size={48} color="#E5E7EB" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ color: '#374151', fontSize: '16px', fontWeight: '500', margin: '0 0 8px 0' }}>
              No Travel Items Yet
            </h3>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              Add travel arrangements for your executives
            </p>
          </div>
        ) : (
          Object.entries(groupedItems).map(([execName, items]) => (
            <div key={execName} style={{ marginBottom: '32px' }}>
              <h2 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
                {execName}
              </h2>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
                {items.map(item => (
                  <div
                    key={item.id}
                    style={{
                      background: '#FFFFFF',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      padding: '20px',
                      transition: 'box-shadow 0.2s',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setSelectedTrip(item);
                      setShowTripModal(true);
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                  >
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: `${getTypeColor(item.type)}20`,
                        color: getTypeColor(item.type),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {getTypeIcon(item.type)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: '0 0 4px 0' }}>
                          {item.trip_name}
                        </h3>
                        <span style={{
                          fontSize: '12px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          background: `${getTypeColor(item.type)}20`,
                          color: getTypeColor(item.type),
                          fontWeight: '500',
                          textTransform: 'capitalize'
                        }}>
                          {item.type.replace('_', ' ')}
                        </span>
                      </div>
                    </div>

                    {/* Details */}
                    <div style={{ marginBottom: '12px' }}>
                      {item.provider && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <MapPin size={14} color="#9CA3AF" />
                          <span style={{ fontSize: '14px', color: '#374151' }}>{item.provider}</span>
                        </div>
                      )}
                      {item.start_date && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <Calendar size={14} color="#9CA3AF" />
                          <span style={{ fontSize: '14px', color: '#374151' }}>
                            {new Date(item.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {item.end_date && ` - ${new Date(item.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                          </span>
                        </div>
                      )}
                      {item.confirmation_number && (
                        <div style={{ marginTop: '12px', padding: '12px', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                          <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 4px 0' }}>Confirmation</p>
                          <p style={{ fontSize: '14px', color: '#0A0A0A', fontWeight: '500', fontFamily: 'monospace', margin: 0 }}>
                            {item.confirmation_number}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {item.notes && (
                      <div style={{ padding: '12px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '8px' }}>
                        <p style={{ fontSize: '13px', color: '#92400E', margin: 0, lineHeight: '1.5' }}>
                          {item.notes}
                        </p>
                      </div>
                    )}

                    {/* Archive Button */}
                    <button
                      onClick={() => handleArchiveClick(item)}
                      style={{
                        marginTop: '12px',
                        padding: '8px 14px',
                        background: item.archived ? '#10B981' : '#F59E0B',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        width: '100%'
                      }}
                    >
                      {item.archived ? 'Unarchive' : 'Archive'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowCreateModal(false)}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid #E5E7EB',
            maxHeight: '90vh',
            overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
              Add Travel Item
            </h3>

            {error && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#991B1B',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Executive *
                </label>
                <select
                  value={formData.executive_id}
                  onChange={(e) => {
                    const newExecId = e.target.value;
                    const execPrefs = executivePreferences[newExecId];
                    // Pre-populate seat preference from executive preferences
                    const seatPref = execPrefs?.seat_preference?.toLowerCase() || '';
                    setFormData({ 
                      ...formData, 
                      executive_id: newExecId,
                      seat_preference: seatPref
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select executive</option>
                  {executives.map(exec => (
                    <option key={exec.id} value={exec.id}>{exec.full_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Trip Name *
                </label>
                <input
                  type="text"
                  value={formData.trip_name}
                  onChange={(e) => setFormData({ ...formData, trip_name: e.target.value })}
                  placeholder="e.g., Boston Trip, Q1 Offsite"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="flight">Flight</option>
                  <option value="hotel">Hotel</option>
                  <option value="car_rental">Car Rental</option>
                  <option value="itinerary">Itinerary</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Provider Dropdown - Smart based on type with executive preferences */}
              {['flight', 'hotel', 'car_rental'].includes(formData.type) && (
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    {formData.type === 'flight' ? 'Airline' : formData.type === 'hotel' ? 'Hotel Chain' : 'Rental Company'} *
                  </label>
                  {(() => {
                    // Get preferred providers from executive preferences
                    const execPrefs = formData.executive_id ? executivePreferences[formData.executive_id] : null;
                    let preferredProviders = [];
                    
                    if (execPrefs) {
                      if (formData.type === 'flight') {
                        preferredProviders = execPrefs.preferred_airlines || [];
                      } else if (formData.type === 'hotel') {
                        preferredProviders = execPrefs.preferred_hotels || [];
                      } else if (formData.type === 'car_rental') {
                        preferredProviders = execPrefs.preferred_car_types || [];
                      }
                    }
                    
                    // Sort options: preferred first, then others
                    const allOptions = PROVIDER_OPTIONS[formData.type] || [];
                    const preferred = allOptions.filter(opt => preferredProviders.includes(opt));
                    const others = allOptions.filter(opt => !preferredProviders.includes(opt));
                    
                    return (
                      <select
                        value={formData.provider}
                        onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select {formData.type === 'flight' ? 'airline' : formData.type === 'hotel' ? 'hotel' : 'company'}...</option>
                        {preferred.length > 0 && (
                          <optgroup label="⭐ Executive Preferred">
                            {preferred.map(option => (
                              <option key={option} value={option}>⭐ {option}</option>
                            ))}
                          </optgroup>
                        )}
                        <optgroup label={preferred.length > 0 ? "Other Options" : "All Options"}>
                          {others.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </optgroup>
                      </select>
                    );
                  })()}
                  {formData.executive_id && executivePreferences[formData.executive_id] && (() => {
                    const execPrefs = executivePreferences[formData.executive_id];
                    let prefList = [];
                    if (formData.type === 'flight') prefList = execPrefs.preferred_airlines || [];
                    else if (formData.type === 'hotel') prefList = execPrefs.preferred_hotels || [];
                    else if (formData.type === 'car_rental') prefList = execPrefs.preferred_car_types || [];
                    
                    if (prefList.length > 0) {
                      return (
                        <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0 0 0' }}>
                          ⭐ Executive prefers: {prefList.join(', ')}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}

              {/* For itinerary/other, keep text input */}
              {!['flight', 'hotel', 'car_rental'].includes(formData.type) && (
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    Provider
                  </label>
                  <input
                    type="text"
                    value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                    placeholder="Provider name"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}

              {/* TSA PreCheck indicator for flights */}
              {formData.type === 'flight' && formData.executive_id && executivePreferences[formData.executive_id]?.tsa_precheck && (
                <div style={{
                  background: '#ECFDF5',
                  border: '1px solid #A7F3D0',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '16px' }}>✅</span>
                  <span style={{ fontSize: '13px', color: '#047857', fontWeight: '500' }}>
                    Executive has TSA PreCheck enabled
                  </span>
                </div>
              )}

              {/* Smart Fields for Flight */}
              {formData.type === 'flight' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Flight Number
                      </label>
                      <input
                        type="text"
                        value={formData.flight_number}
                        onChange={(e) => setFormData({ ...formData, flight_number: e.target.value })}
                        placeholder="e.g., AA1234"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Seat Preference
                        {formData.executive_id && executivePreferences[formData.executive_id]?.seat_preference && (
                          <span style={{ color: '#F59E0B', fontWeight: '400', marginLeft: '4px' }}>
                            (Exec prefers: {executivePreferences[formData.executive_id].seat_preference})
                          </span>
                        )}
                      </label>
                      <select
                        value={formData.seat_preference}
                        onChange={(e) => setFormData({ ...formData, seat_preference: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select preference...</option>
                        <option value="window">Window</option>
                        <option value="aisle">Aisle</option>
                        <option value="middle">Middle</option>
                        <option value="no_preference">No Preference</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Departure Airport
                      </label>
                      <input
                        type="text"
                        value={formData.departure_airport}
                        onChange={(e) => setFormData({ ...formData, departure_airport: e.target.value })}
                        placeholder="e.g., JFK, LAX"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Arrival Airport
                      </label>
                      <input
                        type="text"
                        value={formData.arrival_airport}
                        onChange={(e) => setFormData({ ...formData, arrival_airport: e.target.value })}
                        placeholder="e.g., ORD, SFO"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Smart Fields for Hotel */}
              {formData.type === 'hotel' && (
                <>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                      Hotel Address
                    </label>
                    <input
                      type="text"
                      value={formData.hotel_address}
                      onChange={(e) => setFormData({ ...formData, hotel_address: e.target.value })}
                      placeholder="Full hotel address"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Room Type
                      </label>
                      <select
                        value={formData.room_type}
                        onChange={(e) => setFormData({ ...formData, room_type: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select room type...</option>
                        <option value="standard">Standard</option>
                        <option value="deluxe">Deluxe</option>
                        <option value="suite">Suite</option>
                        <option value="executive">Executive</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Special Requests
                      </label>
                      <input
                        type="text"
                        value={formData.special_requests}
                        onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                        placeholder="e.g., High floor, quiet room"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Check-in Time
                      </label>
                      <input
                        type="time"
                        value={formData.check_in_time}
                        onChange={(e) => setFormData({ ...formData, check_in_time: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Check-out Time
                      </label>
                      <input
                        type="time"
                        value={formData.check_out_time}
                        onChange={(e) => setFormData({ ...formData, check_out_time: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Smart Fields for Car Rental */}
              {formData.type === 'car_rental' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Pickup Location
                      </label>
                      <input
                        type="text"
                        value={formData.pickup_location}
                        onChange={(e) => setFormData({ ...formData, pickup_location: e.target.value })}
                        placeholder="e.g., Airport, City address"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Drop-off Location
                      </label>
                      <input
                        type="text"
                        value={formData.dropoff_location}
                        onChange={(e) => setFormData({ ...formData, dropoff_location: e.target.value })}
                        placeholder="e.g., Airport, City address"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Car Type
                      </label>
                      <select
                        value={formData.car_type}
                        onChange={(e) => setFormData({ ...formData, car_type: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select car type...</option>
                        <option value="economy">Economy</option>
                        <option value="compact">Compact</option>
                        <option value="midsize">Midsize</option>
                        <option value="fullsize">Full-size</option>
                        <option value="suv">SUV</option>
                        <option value="luxury">Luxury</option>
                        <option value="van">Van/Minivan</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Insurance Needed
                      </label>
                      <select
                        value={formData.insurance_needed}
                        onChange={(e) => setFormData({ ...formData, insurance_needed: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select option...</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="already_covered">Already Covered</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Confirmation Number
                </label>
                <input
                  type="text"
                  value={formData.confirmation_number}
                  onChange={(e) => setFormData({ ...formData, confirmation_number: e.target.value })}
                  placeholder="ABC123"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional details..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      <ConfirmModal
        isOpen={showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(false)}
        onConfirm={handleArchive}
        {...CONFIRM_MESSAGES.ARCHIVE_TASK}
        isLoading={isArchiving}
      />

      {/* Trip Detail Modal */}
      {showTripModal && selectedTrip && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}
          onClick={() => setShowTripModal(false)}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              position: 'sticky',
              top: 0,
              background: '#FFFFFF',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              zIndex: 1
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ flex: 1 }}>
                  <h2 style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '700', margin: '0 0 8px 0' }}>
                    {selectedTrip.trip_name}
                  </h2>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: '13px',
                      padding: '4px 12px',
                      borderRadius: '6px',
                      background: `${getTypeColor(selectedTrip.type)}15`,
                      color: getTypeColor(selectedTrip.type),
                      fontWeight: '600'
                    }}>
                      {selectedTrip.type === 'flight' ? '✈️ Flight' : '🚗 Drive'}
                    </span>
                    {selectedTrip.travel_date && (
                      <span style={{
                        fontSize: '13px',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        background: '#F3F4F6',
                        color: '#374151',
                        fontWeight: '500'
                      }}>
                        {new Date(selectedTrip.travel_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {selectedTrip.return_date && ` - ${new Date(selectedTrip.return_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowTripModal(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '24px',
                    color: '#6B7280',
                    cursor: 'pointer',
                    padding: '0',
                    marginLeft: '16px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: '24px' }}>
              {/* Purpose */}
              {selectedTrip.purpose && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Trip Purpose
                  </h3>
                  <p style={{ color: '#0A0A0A', fontSize: '15px', margin: 0, lineHeight: '1.6' }}>
                    {selectedTrip.purpose}
                  </p>
                </div>
              )}

              {/* Travel Details - Type Specific */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {selectedTrip.type === 'flight' && '✈️ Flight Details'}
                  {selectedTrip.type === 'hotel' && '🏨 Hotel Details'}
                  {selectedTrip.type === 'car_rental' && '🚗 Car Rental Details'}
                  {selectedTrip.type === 'other' && '📋 Travel Details'}
                </h3>
                <div style={{
                  background: '#F9FAFB',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #E5E7EB'
                }}>
                  {selectedTrip.type === 'flight' && (
                    <>
                      {selectedTrip.airline && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Airline</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.airline}
                          </p>
                        </div>
                      )}
                      {selectedTrip.flight_number && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Flight Number</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.flight_number}
                          </p>
                        </div>
                      )}
                      {(selectedTrip.departure_airport || selectedTrip.arrival_airport) && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Route</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.departure_airport || 'RDU'} → {selectedTrip.arrival_airport || 'Destination'}
                          </p>
                        </div>
                      )}
                      {selectedTrip.seat_preference && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Seat Preference</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.seat_preference}
                          </p>
                        </div>
                      )}
                      {(selectedTrip.start_date || selectedTrip.end_date) && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Travel Dates</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.start_date && new Date(selectedTrip.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {selectedTrip.end_date && selectedTrip.start_date !== selectedTrip.end_date && ` - ${new Date(selectedTrip.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                          </p>
                        </div>
                      )}
                      {selectedTrip.confirmation_number && selectedTrip.confirmation_number !== 'N/A' && (
                        <div>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Confirmation Number</p>
                          <p style={{ fontSize: '16px', color: '#3B82F6', fontWeight: '700', fontFamily: 'monospace', margin: 0 }}>
                            {selectedTrip.confirmation_number}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  
                  {selectedTrip.type === 'hotel' && (
                    <>
                      {(selectedTrip.hotel_chain || selectedTrip.provider) && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Hotel Chain</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.hotel_chain || selectedTrip.provider}
                          </p>
                        </div>
                      )}
                      {selectedTrip.hotel_address && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Address</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.hotel_address}
                          </p>
                        </div>
                      )}
                      {selectedTrip.room_type && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Room Type</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.room_type}
                          </p>
                        </div>
                      )}
                      {(selectedTrip.check_in_time || selectedTrip.check_out_time) && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Check-in / Check-out</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.check_in_time || '3:00 PM'} / {selectedTrip.check_out_time || '12:00 PM'}
                          </p>
                        </div>
                      )}
                      {(selectedTrip.start_date || selectedTrip.end_date) && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Stay Dates</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.start_date && new Date(selectedTrip.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {selectedTrip.end_date && ` - ${new Date(selectedTrip.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                          </p>
                        </div>
                      )}
                      {selectedTrip.special_requests && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Special Requests</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.special_requests}
                          </p>
                        </div>
                      )}
                      {selectedTrip.confirmation_number && selectedTrip.confirmation_number !== 'N/A' && (
                        <div>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Confirmation Number</p>
                          <p style={{ fontSize: '16px', color: '#3B82F6', fontWeight: '700', fontFamily: 'monospace', margin: 0 }}>
                            {selectedTrip.confirmation_number}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  
                  {selectedTrip.type === 'car_rental' && (
                    <>
                      {(selectedTrip.rental_company || selectedTrip.provider) && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Rental Company</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.rental_company || selectedTrip.provider}
                          </p>
                        </div>
                      )}
                      {(selectedTrip.pickup_location || selectedTrip.drop_off_location) && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Pickup / Drop-off</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.pickup_location || 'Airport'} / {selectedTrip.drop_off_location || 'Airport'}
                          </p>
                        </div>
                      )}
                      {selectedTrip.car_type && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Car Type</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.car_type}
                          </p>
                        </div>
                      )}
                      {selectedTrip.insurance_needed && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Insurance</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.insurance_needed}
                          </p>
                        </div>
                      )}
                      {(selectedTrip.start_date || selectedTrip.end_date) && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Rental Period</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.start_date && new Date(selectedTrip.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {selectedTrip.end_date && ` - ${new Date(selectedTrip.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                          </p>
                        </div>
                      )}
                      {selectedTrip.confirmation_number && selectedTrip.confirmation_number !== 'N/A' && (
                        <div>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Confirmation Number</p>
                          <p style={{ fontSize: '16px', color: '#3B82F6', fontWeight: '700', fontFamily: 'monospace', margin: 0 }}>
                            {selectedTrip.confirmation_number}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  
                  {selectedTrip.type === 'other' && (
                    <>
                      {selectedTrip.provider && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Provider</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.provider}
                          </p>
                        </div>
                      )}
                      {(selectedTrip.start_date || selectedTrip.end_date) && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Travel Dates</p>
                          <p style={{ fontSize: '15px', color: '#0A0A0A', fontWeight: '600', margin: 0 }}>
                            {selectedTrip.start_date && new Date(selectedTrip.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {selectedTrip.end_date && selectedTrip.start_date !== selectedTrip.end_date && ` - ${new Date(selectedTrip.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                          </p>
                        </div>
                      )}
                      {selectedTrip.confirmation_number && selectedTrip.confirmation_number !== 'N/A' && (
                        <div>
                          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 4px 0' }}>Confirmation Number</p>
                          <p style={{ fontSize: '16px', color: '#3B82F6', fontWeight: '700', fontFamily: 'monospace', margin: 0 }}>
                            {selectedTrip.confirmation_number}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Lodging */}
              {selectedTrip.lodging && selectedTrip.lodging !== 'Day trip - no lodging needed' && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Accommodation
                  </h3>
                  <div style={{
                    background: '#FEF3C7',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #FDE68A'
                  }}>
                    <p style={{ fontSize: '15px', color: '#92400E', fontWeight: '600', margin: 0 }}>
                      🏨 {selectedTrip.lodging}
                    </p>
                  </div>
                </div>
              )}

              {/* Ground Transportation */}
              {selectedTrip.ground_transport && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Ground Transportation
                  </h3>
                  <div style={{
                    background: '#F3F4F6',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <p style={{ fontSize: '14px', color: '#374151', margin: 0, lineHeight: '1.6' }}>
                      {selectedTrip.ground_transport}
                    </p>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedTrip.notes && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Important Notes
                  </h3>
                  <div style={{
                    background: '#F0F9FF',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #BAE6FD'
                  }}>
                    <p style={{ fontSize: '14px', color: '#0C4A6E', margin: 0, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                      {selectedTrip.notes}
                    </p>
                  </div>
                </div>
              )}

              {/* Executive Travel Preferences */}
              {selectedTrip.executive_id && executivePreferences[selectedTrip.executive_id] && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    👤 Executive Travel Preferences
                  </h3>
                  <div style={{
                    background: '#FEF3C7',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #FDE68A'
                  }}>
                    {(() => {
                      const prefs = executivePreferences[selectedTrip.executive_id];
                      const hasPrefs = prefs.seat_preference || prefs.tsa_precheck || 
                                       (prefs.preferred_airlines && prefs.preferred_airlines.length > 0) ||
                                       (prefs.preferred_hotels && prefs.preferred_hotels.length > 0) ||
                                       (prefs.preferred_car_types && prefs.preferred_car_types.length > 0);
                      
                      if (!hasPrefs) {
                        return (
                          <p style={{ fontSize: '14px', color: '#92400E', margin: 0, fontStyle: 'italic' }}>
                            No travel preferences set for this executive. Configure preferences in the Executive Hub.
                          </p>
                        );
                      }
                      
                      return (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                          {prefs.seat_preference && (
                            <div>
                              <p style={{ fontSize: '12px', color: '#92400E', margin: '0 0 4px 0', fontWeight: '500' }}>Seat Preference</p>
                              <p style={{ fontSize: '14px', color: '#78350F', margin: 0, fontWeight: '600' }}>
                                🪑 {prefs.seat_preference}
                              </p>
                            </div>
                          )}
                          <div>
                            <p style={{ fontSize: '12px', color: '#92400E', margin: '0 0 4px 0', fontWeight: '500' }}>TSA PreCheck</p>
                            <p style={{ fontSize: '14px', color: '#78350F', margin: 0, fontWeight: '600' }}>
                              {prefs.tsa_precheck ? '✅ Yes' : '❌ No'}
                            </p>
                          </div>
                          {prefs.preferred_airlines && prefs.preferred_airlines.length > 0 && (
                            <div>
                              <p style={{ fontSize: '12px', color: '#92400E', margin: '0 0 4px 0', fontWeight: '500' }}>Preferred Airlines</p>
                              <p style={{ fontSize: '14px', color: '#78350F', margin: 0, fontWeight: '600' }}>
                                ✈️ {prefs.preferred_airlines.join(', ')}
                              </p>
                            </div>
                          )}
                          {prefs.preferred_hotels && prefs.preferred_hotels.length > 0 && (
                            <div>
                              <p style={{ fontSize: '12px', color: '#92400E', margin: '0 0 4px 0', fontWeight: '500' }}>Preferred Hotels</p>
                              <p style={{ fontSize: '14px', color: '#78350F', margin: 0, fontWeight: '600' }}>
                                🏨 {prefs.preferred_hotels.join(', ')}
                              </p>
                            </div>
                          )}
                          {prefs.preferred_car_types && prefs.preferred_car_types.length > 0 && (
                            <div>
                              <p style={{ fontSize: '12px', color: '#92400E', margin: '0 0 4px 0', fontWeight: '500' }}>Preferred Car Types</p>
                              <p style={{ fontSize: '14px', color: '#78350F', margin: 0, fontWeight: '600' }}>
                                🚗 {prefs.preferred_car_types.join(', ')}
                              </p>
                            </div>
                          )}
                          {prefs.loyalty_programs && Object.keys(prefs.loyalty_programs).length > 0 && (
                            <div style={{ gridColumn: 'span 2' }}>
                              <p style={{ fontSize: '12px', color: '#92400E', margin: '0 0 4px 0', fontWeight: '500' }}>Loyalty Programs</p>
                              <p style={{ fontSize: '14px', color: '#78350F', margin: 0, fontWeight: '600' }}>
                                🎫 {Object.entries(prefs.loyalty_programs).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
                <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Quick Actions
                </h3>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    style={{
                      padding: '10px 16px',
                      background: '#3B82F6',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      success('Coming Soon', 'Task creation from travel items is coming soon!');
                    }}
                  >
                    <AlertCircle size={16} />
                    Add Reminder Task
                  </button>
                  <button
                    style={{
                      padding: '10px 16px',
                      background: '#10B981',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      success('Coming Soon', 'Receipt upload is coming soon!');
                    }}
                  >
                    <FileText size={16} />
                    Upload Receipt
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #E5E7EB',
              background: '#F9FAFB',
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchiveClick(selectedTrip);
                  setShowTripModal(false);
                }}
                style={{
                  padding: '8px 16px',
                  background: 'transparent',
                  color: '#DC2626',
                  border: '1px solid #FCA5A5',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Archive Trip
              </button>
              <button
                onClick={() => setShowTripModal(false)}
                style={{
                  padding: '8px 24px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelBinderPage;
